import * as React from 'react';
import { useNavigate } from 'react-router';
import type { PiletApi } from '@chess/platform-web';
import { create } from 'zustand';
import { CHESS_API, type Chess } from '@chess/api-chess';
import { GAMES_API, reviewPath, type Games } from '@chess/api-games';
import { OPENINGS_API, type Openings } from '@chess/api-openings';
import { ANALYSIS_API, type Analysis } from '@chess/api-analysis';
import { STORAGE_API, type Storage } from '@chess/api-storage';
import { LOG_API, type Log } from '@chess/api-log';
import { REVIEW_API, type ReviewTabs, type ReviewWorkspace } from '@chess/api-review';
import { STORE_API, type GameStore } from '@chess/api-model';
import { createOpeningHistory, type OpeningHistory, type SearchResult } from './service.ts';
import { pawns, scoreText, type Row } from './score.ts';
import { SCAN_API, scanSource, type ScanService } from '@chess/api-scan';
import './history.css';

// The opening history: how the reader has done in the opening the review is
// playing. A section in the Opening tab, contributed to the `review.opening.sections`
// slot: a table of the latest games through the same named position, each
// with the score lost coming out of the book and a way to open it on the
// board without leaving the review. The paths it needs are computed from
// the moves on first sight and kept; the scores come from stored scans.
// Every game a search returns without a scan, on the first page or after
// "Search more", is scanned automatically, one at a time, through the app-wide scan queue (`shared-scan`).

const PAGE = 50;

/**
 * Which game the history table describes while board tabs are open.
 * `'pinned'` (default): the current game in the pinned tab; the table keeps
 * its rows and order when a history game is shown in another tab.
 * `'active'`: whichever tab is showing; the table follows it.
 */
export type HistoryFollows = 'pinned' | 'active';
export const HISTORY_FOLLOWS = 'pinned' as HistoryFollows;

interface Node {
  epd: string;
  eco: string;
  name: string;
}

interface SectionParams {
  gameId?: string;
  node?: Node;
  /** Where the section sits among the opening tab's sections. */
  order?: number;
}

type Status = 'idle' | 'searching' | 'ready' | 'failed';

interface HistoryState {
  status: Status;
  epd?: string;
  /** The game the result was searched for, so a remount does not search again. */
  forGame?: string;
  size: number;
  result?: SearchResult;
  /**
   * The pinned tab's game and opening node, kept while a history game is open
   * in another board tab: the table always describes the game being reviewed,
   * never the history game shown beside it.
   */
  anchor?: { gameId?: string; node?: Node };
}

const useHistoryStore = create<HistoryState>(() => ({ status: 'idle', size: PAGE }));

export function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso.slice(0, 10);
  return date.toLocaleDateString(undefined, { year: '2-digit', month: 'short', day: 'numeric' });
}

function resultForReader(row: Row): string {
  if (row.result === '1/2-1/2') return '½';
  if (row.result === '*') return '';
  const won = (row.result === '1-0') === (row.reader === 'white');
  return won ? 'won' : 'lost';
}

/** Resolves once the game's scan is not running: at once, or on its completion or failure. */
function untilScanned(analysis: Analysis, gameId: string): Promise<void> {
  if (!analysis.status(gameId)?.running) return Promise.resolve();
  return new Promise((resolve) => {
    const stop = analysis.subscribe((event) => {
      if (event.gameId !== gameId || event.type === 'progress') return;
      stop();
      resolve();
    });
    if (!analysis.status(gameId)?.running) {
      stop();
      resolve();
    }
  });
}

/** The score cell of a game without a score yet: where it is in the automatic scan. */
const Pending: React.FC<{ state?: 'queued' | 'scanning' | 'failed'; current?: boolean; onScan?: () => void }> = ({ state, current, onScan }) => {
  if (state === 'scanning' || current) return <span className="history-pending">scanning…</span>;
  if (!onScan) return <span className="history-pending">—</span>;
  // Queued, failed or not queued yet: the button moves the game to the front of the queue.
  return (
    <button
      type="button"
      className="btn btn-ghost history-scan"
      onClick={(e) => {
        e.stopPropagation();
        onScan();
      }}
      title={state === 'queued' ? 'Waiting for the engine · scan this game next' : state === 'failed' ? 'The scan did not complete · try again' : 'Scan this game next'}>
      {state === 'queued' ? 'queued' : 'scan'}
    </button>
  );
};

/** The shared stage section frame (`review.stageSection`, from review-stage-section). */
type FrameProps = { title: string; aside?: string; tooltip?: string; children?: React.ReactNode };

/** The board tabs, followed; undefined without a workspace (an older review screen). */
function useTabs(workspace?: ReviewWorkspace): ReviewTabs | undefined {
  const [tabs, setTabs] = React.useState<ReviewTabs | undefined>(() => workspace?.tabs());
  React.useEffect(() => {
    if (!workspace) return;
    setTabs(workspace.tabs());
    return workspace.subscribeTabs(setTabs);
  }, [workspace]);
  return tabs;
}

/** The history's games in the app-wide scan queue: held back while the review is elsewhere. */
const HISTORY = scanSource('history-openings');

const Section: React.FC<{ params: SectionParams; history: OpeningHistory; analysis?: Analysis; queue?: ScanService; log?: Log; Frame: React.FC<FrameProps>; workspace?: ReviewWorkspace }> = ({ params: live, history, analysis, queue, log, Frame, workspace }) => {
  const s = useHistoryStore();
  const navigate = useNavigate();
  const tabs = useTabs(workspace);
  const activeTab = tabs?.tabs.find((t) => t.id === tabs.activeId);
  // With `HISTORY_FOLLOWS = 'active'` every tab counts as the anchor, so the
  // table follows whichever tab is showing.
  const pinnedActive = HISTORY_FOLLOWS === 'active' || !activeTab || activeTab.pinned;
  // While the pinned tab shows, its game and node are the anchor; while a
  // history game is shown in another tab, the anchor holds, so the table, its
  // rows and its search do not change.
  React.useEffect(() => {
    if (!pinnedActive) return;
    const anchor = useHistoryStore.getState().anchor;
    if (anchor?.gameId === live.gameId && anchor?.node?.epd === live.node?.epd) return;
    useHistoryStore.setState({ anchor: { gameId: live.gameId, node: live.node } });
  }, [pinnedActive, live.gameId, live.node?.epd]);
  const params: SectionParams = pinnedActive ? live : { ...live, ...(s.anchor ?? {}) };
  // Games open in unpinned board tabs, and the one showing now.
  const openGames = new Set((tabs?.tabs ?? []).filter((t) => !t.pinned && t.gameId).map((t) => t.gameId!));
  const activeGame = activeTab && !activeTab.pinned ? activeTab.gameId : undefined;
  const epd = params.node?.epd;
  // Re-render when the queue's state changes.
  const [, setTick] = React.useState(0);
  React.useEffect(() => queue?.subscribe(() => setTick((n) => n + 1)), [queue]);

  // The open game's own scan goes first; the history's games in the shared
  // queue run while this history is shown and are held back when the review
  // leaves it, resuming when it is shown again. Other sources run on.
  React.useEffect(() => {
    if (!queue || !analysis) return;
    let live = true;
    queue.pause(HISTORY);
    const openGame = params.gameId;
    void (openGame ? untilScanned(analysis, openGame) : Promise.resolve()).then(() => live && queue.resume(HISTORY));
    return () => {
      live = false;
      queue.pause(HISTORY);
    };
  }, [queue, analysis, params.gameId]);

  const run = React.useCallback(
    async (size: number) => {
      if (!epd) return;
      useHistoryStore.setState({ status: 'searching', epd, size, forGame: params.gameId });
      try {
        const result = await history.search(epd, size, params.gameId);
        if (useHistoryStore.getState().epd !== epd) return;
        useHistoryStore.setState({ status: 'ready', result });
        // Scan what this page brought without a score, newest first.
        queue?.enqueue(result.rows.filter((r) => r.score.kind === 'unscanned' && !r.current).map((r) => r.gameId), HISTORY);
      } catch (error) {
        log?.warn('opening history search failed', { error: String(error) });
        useHistoryStore.setState({ status: 'failed' });
      }
    },
    [epd, params.gameId, history, log, queue],
  );

  React.useEffect(() => {
    // The section remounts when a board tab or stage tab changes; a result
    // already searched for this game and node is kept, not searched again.
    const now = useHistoryStore.getState();
    if (now.result && now.epd === epd && now.forGame === params.gameId) return;
    void run(PAGE);
  }, [run, epd, params.gameId]);

  // A row refreshes when its scan completes, whoever started it.
  React.useEffect(() => {
    if (!analysis) return;
    return analysis.subscribe((event) => {
      if (event.type !== 'complete') return;
      const entry = history.entry(event.gameId);
      const current = useHistoryStore.getState().result;
      if (!entry || !current || !current.rows.some((r) => r.gameId === event.gameId)) return;
      void history.row(entry, params.gameId).then((row) => {
        if (!row) return;
        const latest = useHistoryStore.getState().result;
        if (!latest) return;
        useHistoryStore.setState({ result: { ...latest, rows: latest.rows.map((r) => (r.gameId === row.gameId ? row : r)) } });
      });
    });
  }, [analysis, history, params.gameId]);

  // A row opens its game in a board tab beside the one being reviewed, at the
  // position before the loss; the game the reader came from stays in its
  // pinned tab. Without the workspace (an older review screen) it navigates.
  const go = (row: Row) => {
    const entry = history.entry(row.gameId);
    if (!entry) return;
    if (workspace?.openTab) {
      // A game already open in a board tab: show that tab as the reader left
      // it. Otherwise open a new tab at the position before the loss.
      const open = workspace.tabs().tabs.find((t) => !t.pinned && t.gameId === entry.gameId);
      if (open) {
        workspace.activate(open.id);
        return;
      }
      void workspace.openTab(entry.gameId, row.goPly, row.score.kind === 'score' ? scoreText(row.score.score.score) : undefined);
      return;
    }
    navigate(`${reviewPath({ id: entry.gameId, owner: entry.owner, source: entry.source, playedAt: entry.playedAt })}#ply=${row.goPly}`);
  };

  if (!params.node) return null;
  // The current game first, above the worst-first rows: the way back to the
  // pinned board tab from any history tab.
  const all = s.result?.rows ?? [];
  const rows = [...all.filter((r) => r.current), ...all.filter((r) => !r.current)];
  const pinnedTab = tabs?.tabs.find((t) => t.pinned);
  const pinnedShowing = !activeTab || activeTab.pinned;
  const showCurrent = () => {
    if (workspace && pinnedTab) workspace.activate(pinnedTab.id);
  };
  // An empty section is not shown: nothing while searching, on failure, or with no games.
  if (rows.length === 0) return null;
  const searchedAll = !!s.result && s.result.searched >= s.result.total;
  // The shared queue's progress, shown while it holds one of this history's games.
  const mine = rows.some((r) => queue?.state(r.gameId) === 'queued' || queue?.state(r.gameId) === 'scanning');
  const progress = mine && queue?.progress().running ? queue.progress() : undefined;
  return (
    <Frame
      title="History"
      tooltip={`Your games through ${params.node.name}`}
      aside={progress ? `Scanning ${Math.min(progress.done + 1, progress.total)} of ${progress.total}` : undefined}>
      <div className="history-openings">
      {(
        <table className="history-table">
          <thead>
            <tr>
              <th scope="col">Date</th>
              <th scope="col" className="history-score" title="Your score at your first move after leaving the book">Score</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const open = (): void => (row.current ? showCurrent() : go(row));
              const active = row.current ? pinnedShowing : row.gameId === activeGame;
              const className = [row.current ? 'history-row history-current' : 'history-row', !row.current && openGames.has(row.gameId) ? 'history-row-open' : '', active ? 'history-row-active' : ''].filter(Boolean).join(' ');
              return (
              <tr
                key={row.gameId}
                className={className}
                aria-current={active ? 'true' : undefined}
                title={row.current ? `The game you are reviewing · ${formatDate(row.playedAt)}` : `${resultForReader(row)} · open this game in a board tab at the position before the loss`}
                tabIndex={0}
                onClick={open}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    open();
                  }
                }}>
                <td className="history-date" title={formatDate(row.playedAt)}>{row.current ? 'Current game' : formatDate(row.playedAt)}</td>
                <td className="history-score">
                  {row.score.kind === 'score' && (
                    <span className={pawns(row.score.score.score) <= -1 ? 'history-loss history-loss-big' : 'history-loss'} title={`${scoreText(row.score.score.score)} at your first move after leaving the book, from your side`}>
                      {scoreText(row.score.score.score)}
                    </span>
                  )}
                  {row.score.kind !== 'score' && <Pending state={row.score.kind === 'scanning' ? 'scanning' : queue?.state(row.gameId)} current={row.current} onScan={queue ? () => queue.prioritize(row.gameId, HISTORY) : undefined} />}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <p className="history-foot">
        {s.status === 'searching' && !s.result && <span>Searching…</span>}
        {s.result && (
          <span>
            Searched {s.result.searched} of {s.result.total}
          </span>
        )}
        {s.result && !searchedAll && (
          <button type="button" className="btn btn-ghost history-more" onClick={() => void run(s.size + PAGE)} disabled={s.status === 'searching'}>
            {s.status === 'searching' ? 'Searching…' : 'Search more'}
          </button>
        )}
      </p>
      </div>
    </Frame>
  );
};

export function setup(app: PiletApi) {
  let log: Log | undefined;
  void app.getApi<Log>(LOG_API).then((l) => (log = l.child(app.meta.name)));

  const ready = (async () => {
    const [store, games, chess, openings, storage] = await Promise.all([
      app.getApi<GameStore>(STORE_API),
      app.getApi<Games>(GAMES_API),
      app.getApi<Chess>(CHESS_API),
      app.getApi<Openings>(OPENINGS_API),
      app.getApi<Storage>(STORAGE_API),
    ]);
    // The engine is optional: without it the table has no scores.
    const analysis = await Promise.race([
      app.getApi<Analysis>(ANALYSIS_API),
      new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), 2000)),
    ]).catch(() => undefined);
    const history = createOpeningHistory({ store, games, chess, openings, analysis, storage: storage.scope('history-openings'), log });
    // The app-wide scan queue (plugin `shared-scan`), so the history's scans
    // and the automatic scan share one engine and one list.
    const queue = analysis
      ? await Promise.race([
          app.getApi<ScanService>(SCAN_API),
          new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), 2000)),
        ]).catch(() => undefined)
      : undefined;
    return { history, analysis, queue };
  })();

  // The section's frame, title and spacing come from review-stage-section.
  const Frame: React.FC<FrameProps> = ({ title, aside, tooltip, children }) => (
    <app.Extension name="review.stageSection" params={{ title, aside, tooltip }}>
      {children}
    </app.Extension>
  );

  let workspace: ReviewWorkspace | undefined;
  void app.getApi<ReviewWorkspace>(REVIEW_API).then((w) => (workspace = w));

  app.registerExtension<SectionParams>('review.opening.sections', ({ params }) => {
    const [deps, setDeps] = React.useState<{ history: OpeningHistory; analysis?: Analysis; queue?: ScanService }>();
    React.useEffect(() => {
      void ready.then(setDeps);
    }, []);
    if (!deps) return null;
    return <Section params={params} history={deps.history} analysis={deps.analysis} queue={deps.queue} log={log} Frame={Frame} workspace={workspace} />;
  }, { order: 20 });
}
